/**
 * Assurance Readiness Page - Production Version
 * Real-time assessment of audit and external assurance readiness
 * Uses new /api/v1/assurance/readiness endpoint
 * Implements Superpower #3: Audit Defensibility
 */
import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2,
  AlertTriangle,
  Clock,
  Shield,
  FileText,
  Users,
  BarChart3,
  Loader2,
  RefreshCw,
  Hash,
  Package,
  ArrowRight,
  XCircle,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  History
} from 'lucide-react';
import { apiFetch } from '../../services/api_service';
import { JourneySection, JourneyPanel } from '../journeys/JourneyPanel';
import { AuditorPortal as AuditorPortalJourney, RAGASEvaluation } from '../journeys/JourneyAudit';

// Score Circle Component
function ScoreCircle({ score, size = 'lg' }) {
  const getScoreColor = (s) => {
    if (s >= 90) return { bg: 'bg-emerald-500/20', border: 'border-emerald-500', text: 'text-emerald-400' };
    if (s >= 70) return { bg: 'bg-amber-500/20', border: 'border-amber-500', text: 'text-amber-400' };
    return { bg: 'bg-red-500/20', border: 'border-red-500', text: 'text-red-400' };
  };
  
  const colors = getScoreColor(score);
  const sizeClasses = size === 'lg' 
    ? 'h-32 w-32 border-8 text-4xl' 
    : 'h-20 w-20 border-4 text-2xl';

  return (
    <div className={`${sizeClasses} rounded-full flex items-center justify-center ${colors.bg} ${colors.border}`}>
      <span className={`font-bold ${colors.text}`}>{score}%</span>
    </div>
  );
}

// Dimension Card Component
function DimensionCard({ icon: Icon, title, score, description, highlight, color = 'emerald' }) {
  const colorClasses = {
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    purple: 'text-purple-400',
    amber: 'text-amber-400',
    teal: 'text-teal-400'
  };
  
  const progressColor = score >= 90 ? 'bg-emerald-500' : score >= 70 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className={`bg-[#111111] rounded-xl border ${highlight ? 'border-teal-500/50 ring-1 ring-teal-500/30' : 'border-[#1F1F1F]'} p-6`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${colorClasses[color]}`} />
          <h3 className="text-base font-semibold text-white">{title}</h3>
        </div>
        <span className={`text-2xl font-bold ${score >= 90 ? 'text-emerald-400' : score >= 70 ? 'text-amber-400' : 'text-red-400'}`}>
          {score}%
        </span>
      </div>

      <div className="h-3 bg-[#1F1F1F] rounded-full overflow-hidden mb-3">
        <div
          className={`h-full ${progressColor} rounded-full transition-all duration-500`}
          style={{ width: `${score}%` }}
        />
      </div>

      <p className="text-sm text-[#A0A0A0]">{description}</p>
      
      {highlight && (
        <div className="mt-3 px-2 py-1 rounded bg-teal-500/20 border border-teal-500/30 text-xs font-semibold text-teal-400 inline-block">
          {highlight}
        </div>
      )}
    </div>
  );
}

// Issue Card Component
function IssueCard({ issue, onResolve }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const severityClasses = {
    critical: 'bg-red-500/10 border-l-red-500',
    high: 'bg-orange-500/10 border-l-orange-500',
    medium: 'bg-amber-500/10 border-l-amber-500',
    low: 'bg-blue-500/10 border-l-blue-500'
  };
  
  const badgeClasses = {
    critical: 'bg-red-500/20 text-red-400',
    high: 'bg-orange-500/20 text-orange-400',
    medium: 'bg-amber-500/20 text-amber-400',
    low: 'bg-blue-500/20 text-blue-400'
  };

  return (
    <div className={`p-4 rounded-lg border-l-4 ${severityClasses[issue.severity]}`} data-testid={`issue-${issue.issue_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${badgeClasses[issue.severity]}`}>
              {issue.severity.toUpperCase()}
            </span>
            <span className="text-xs text-[#6B6B6B]">{issue.category?.replace(/_/g, ' ')}</span>
          </div>
          
          <p className="text-sm font-semibold text-white mb-1">{issue.title}</p>
          <p className="text-xs text-[#A0A0A0] mb-2">{issue.description}</p>
          
          <div className="flex items-center gap-4 text-xs text-[#6B6B6B]">
            {issue.affected_claims?.length > 0 && (
              <span>📋 {issue.affected_claims.length} claim(s) affected</span>
            )}
            {issue.due_date && (
              <span>📅 Due: {new Date(issue.due_date).toLocaleDateString()}</span>
            )}
          </div>

          {issue.recommendation && (
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 text-xs text-teal-400 mt-2 hover:underline"
            >
              {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {isExpanded ? 'Hide' : 'Show'} recommendation
            </button>
          )}
          
          {isExpanded && issue.recommendation && (
            <div className="mt-3 p-3 rounded bg-[#0A0A0A] border border-[#1F1F1F] text-xs">
              <span className="font-semibold text-white">💡 Recommendation: </span>
              <span className="text-[#A0A0A0]">{issue.recommendation}</span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 shrink-0">
          {issue.status !== 'resolved' && (
            <button
              onClick={() => onResolve(issue)}
              className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm transition-colors flex items-center gap-2"
              data-testid={`resolve-issue-${issue.issue_id}`}
            >
              Resolve
              <ArrowRight size={14} />
            </button>
          )}
          {issue.affected_claims?.length > 0 && (
            <button className="px-4 py-2 rounded-lg border border-[#2F2F2F] hover:bg-[#1F1F1F] text-white text-sm transition-colors">
              View Claims
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Recommendation Card Component
function RecommendationCard({ rec, onAction }) {
  const priorityStyles = {
    critical: 'bg-red-500/10 border-red-500/30',
    high: 'bg-orange-500/10 border-orange-500/30',
    medium: 'bg-amber-500/10 border-amber-500/30',
    success: 'bg-emerald-500/10 border-emerald-500/30'
  };

  return (
    <div className={`flex items-start gap-3 p-4 rounded-lg border ${priorityStyles[rec.priority] || 'bg-[#111111] border-[#1F1F1F]'}`}>
      <div className={`p-2 rounded-lg ${rec.priority === 'success' ? 'bg-emerald-500/20' : 'bg-[#1F1F1F]'}`}>
        <Package className={`h-5 w-5 ${rec.priority === 'success' ? 'text-emerald-400' : 'text-[#A0A0A0]'}`} />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-white">{rec.action}</p>
        <p className="text-xs text-[#6B6B6B] mt-1">{rec.description}</p>
        <div className="flex items-center gap-4 mt-2 text-xs text-[#6B6B6B]">
          {rec.estimated_time_to_resolve && (
            <span>⏱️ {rec.estimated_time_to_resolve}</span>
          )}
          {rec.impact && (
            <span>📈 {rec.impact}</span>
          )}
        </div>
      </div>
      <button 
        onClick={() => onAction && onAction(rec)}
        className={`px-4 py-2 rounded-lg text-sm transition-colors ${
          rec.priority === 'success'
            ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
            : 'bg-[#1F1F1F] hover:bg-[#2F2F2F] text-white'
        }`}
      >
        Take Action →
      </button>
    </div>
  );
}

// History Chart (Simple SVG line chart)
function ReadinessHistoryChart({ history }) {
  if (!history || history.length < 2) {
    return (
      <div className="flex items-center justify-center h-64 text-[#6B6B6B] text-sm">
        Not enough data for trend chart
      </div>
    );
  }

  const reversedHistory = [...history].reverse();
  const maxScore = 100;
  const padding = 40;
  const chartWidth = 800;
  const chartHeight = 200;
  
  const points = reversedHistory.map((snapshot, idx) => {
    const x = padding + (idx / (reversedHistory.length - 1)) * (chartWidth - padding * 2);
    const y = chartHeight - padding - (snapshot.overall_score / maxScore) * (chartHeight - padding * 2);
    return { x, y, score: snapshot.overall_score, date: snapshot.snapshot_date };
  });

  const pathD = points.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <div className="relative overflow-x-auto">
      <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-64">
        {/* Grid lines */}
        {[25, 50, 75, 100].map(val => {
          const y = chartHeight - padding - (val / maxScore) * (chartHeight - padding * 2);
          return (
            <g key={val}>
              <line x1={padding} y1={y} x2={chartWidth - padding} y2={y} stroke="#1F1F1F" strokeDasharray="4" />
              <text x={padding - 10} y={y + 4} textAnchor="end" fill="#6B6B6B" fontSize="10">{val}%</text>
            </g>
          );
        })}
        
        {/* Line */}
        <path d={pathD} fill="none" stroke="#10b981" strokeWidth="3" />
        
        {/* Points */}
        {points.map((p, idx) => (
          <g key={idx}>
            <circle cx={p.x} cy={p.y} r="6" fill="#10b981" stroke="white" strokeWidth="2" />
            <text x={p.x} y={p.y - 12} textAnchor="middle" fill="#A0A0A0" fontSize="10" fontWeight="600">
              {p.score}%
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function AssuranceReadinessPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [history, setHistory] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [resolvingIssue, setResolvingIssue] = useState(null);

  // Fetch disclosure cycles
  const fetchCycles = async () => {
    try {
      const data = await apiFetch('/disclosure-cycles/list?limit=20');
      if (!data?._error) {
        setCycles(data.items || []);
        if (data.items?.length > 0 && !selectedCycle) {
          setSelectedCycle(data.items[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching cycles:', err);
    }
  };

  // Fetch readiness data
  const fetchReadiness = async (cycleId) => {
    if (!cycleId) return;
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiFetch(`/assurance/readiness?cycle_id=${cycleId}`);
      if (data?._error) throw new Error(data.message || 'Failed to fetch readiness data');
      setReadiness(data);
    } catch (err) {
      console.error('Error fetching readiness:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch readiness history
  const fetchHistory = async (cycleId) => {
    if (!cycleId) return;
    try {
      const data = await apiFetch(`/assurance/readiness/history?cycle_id=${cycleId}&limit=10`);
      if (!data?._error) {
        setHistory(data.snapshots || []);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  // Initial load
  useEffect(() => {
    fetchCycles();
  }, []);

  // Load readiness when cycle changes
  useEffect(() => {
    if (selectedCycle) {
      fetchReadiness(selectedCycle);
      fetchHistory(selectedCycle);
    }
  }, [selectedCycle]);

  // Handle issue resolution
  const handleResolveIssue = async (issue) => {
    const resolution = prompt('How was this issue resolved?');
    if (!resolution) return;
    
    setResolvingIssue(issue.issue_id);
    
    try {
      const data = await apiFetch(`/assurance/readiness/${issue.issue_id}/resolve`, {
        method: 'POST',
        body: { resolution }
      });
      
      if (!data?._error) {
        await fetchReadiness(selectedCycle);
        alert('Issue marked as resolved');
      } else {
        throw new Error(data.message || 'Failed to resolve issue');
      }
    } catch (err) {
      console.error('Error resolving issue:', err);
      alert('Failed to resolve issue');
    } finally {
      setResolvingIssue(null);
    }
  };

  // Get all issues flattened
  const getAllIssues = () => {
    if (!readiness?.issues) return [];
    return [
      ...(readiness.issues.critical || []),
      ...(readiness.issues.high || []),
      ...(readiness.issues.medium || []),
      ...(readiness.issues.low || [])
    ];
  };

  const issues = getAllIssues();

  if (loading && !readiness) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (error && !readiness) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
          <AlertTriangle className="h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">Error Loading Readiness Data</h3>
          <p className="text-sm text-[#6B6B6B] mb-4">{error}</p>
          <button
            onClick={() => fetchReadiness(selectedCycle)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg"
          >
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
        <JourneySection title="Live Journeys">
          <JourneyPanel title="Auditor Verification Portal" ucId="UC-13"><AuditorPortalJourney /></JourneyPanel>
          <JourneyPanel title="RAGAS AI Quality Evaluation" ucId="UC-14"><RAGASEvaluation /></JourneyPanel>
        </JourneySection>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assurance-readiness-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-700 rounded-xl p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-sm text-white/80 mb-2 flex items-center gap-2">
              <Shield size={14} />
              Audit & Assurance
              <span className="mx-1">›</span>
              Assurance Readiness
            </p>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <CheckCircle2 className="h-7 w-7" />
              Assurance Readiness
              <span className="px-3 py-1 rounded-full bg-white/20 text-white text-sm font-medium">
                Superpower #3
              </span>
            </h1>
            <p className="text-white/90 mt-2">
              Real-time assessment of audit and external assurance readiness
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                showHistory 
                  ? 'bg-white/20 border-white/40 text-white' 
                  : 'border-white/30 text-white hover:bg-white/10'
              }`}
            >
              <History size={16} />
              {showHistory ? 'Hide' : 'Show'} Trend
            </button>
            <button
              onClick={() => fetchReadiness(selectedCycle)}
              disabled={loading}
              className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Cycle Selector */}
        <div className="flex items-center gap-4">
          <label className="text-sm text-white/80">Disclosure Cycle:</label>
          <select
            value={selectedCycle}
            onChange={(e) => setSelectedCycle(e.target.value)}
            className="px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white text-sm min-w-[300px] focus:outline-none focus:border-white/50"
            data-testid="cycle-selector"
          >
            {cycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id} className="text-gray-900">
                {cycle.cycle_name}
              </option>
            ))}
          </select>
        </div>

        {/* Quick Stats */}
        {readiness && (
          <div className="grid md:grid-cols-4 gap-4 mt-6">
            <div className="p-4 rounded-lg bg-white/10 border border-white/20">
              <p className="text-xs text-white/80 mb-1">Overall Score</p>
              <p className={`text-3xl font-bold ${
                readiness.overall_score >= 90 ? 'text-emerald-300' :
                readiness.overall_score >= 70 ? 'text-amber-300' : 'text-red-300'
              }`}>
                {readiness.overall_score}%
              </p>
              <p className="text-xs text-white/70 mt-1">
                {readiness.is_audit_ready ? '✓ Audit Ready' : 'Needs Improvement'}
              </p>
            </div>

            <div className="p-4 rounded-lg bg-white/10 border border-white/20">
              <p className="text-xs text-white/80 mb-1">Critical Issues</p>
              <p className="text-3xl font-bold text-white">
                {readiness.issues?.critical?.length || 0}
              </p>
              <p className="text-xs text-white/70 mt-1">Must resolve immediately</p>
            </div>

            <div className="p-4 rounded-lg bg-white/10 border border-white/20">
              <p className="text-xs text-white/80 mb-1">Claims with Evidence</p>
              <p className="text-3xl font-bold text-white">
                {readiness.metrics?.claims_with_primary_evidence || 0}
              </p>
              <p className="text-xs text-white/70 mt-1">
                of {readiness.metrics?.total_claims || 0} total
              </p>
            </div>

            <div className="p-4 rounded-lg bg-white/10 border border-white/20">
              <p className="text-xs text-white/80 mb-1">Approved Claims</p>
              <p className="text-3xl font-bold text-white">
                {readiness.metrics?.claims_fully_approved || 0}
              </p>
              <p className="text-xs text-white/70 mt-1">
                {readiness.metrics?.total_claims > 0 
                  ? Math.round((readiness.metrics.claims_fully_approved / readiness.metrics.total_claims) * 100)
                  : 0}% complete
              </p>
            </div>
          </div>
        )}
      </div>

      {readiness && (
        <>
          {/* History Chart */}
          {showHistory && history.length > 0 && (
            <div className="bg-[#111111] rounded-xl border border-[#1F1F1F] p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-400" />
                Readiness Trend
              </h3>
              <ReadinessHistoryChart history={history} />
            </div>
          )}

          {/* Overall Status Card */}
          <div className={`rounded-xl border-2 p-8 ${
            readiness.is_audit_ready 
              ? 'bg-emerald-500/10 border-emerald-500/30' 
              : readiness.overall_score >= 70 
              ? 'bg-amber-500/10 border-amber-500/30' 
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white mb-2">
                  Overall Assurance Readiness
                </h2>
                <p className="text-[#A0A0A0]">
                  Based on {readiness.metrics?.total_claims || 0} disclosure claims
                </p>

                {readiness.is_audit_ready ? (
                  <div className="mt-4 p-4 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center gap-3">
                    <CheckCircle2 className="h-8 w-8 text-emerald-400 shrink-0" />
                    <div>
                      <p className="text-base font-semibold text-emerald-400">
                        ✓ This disclosure cycle is audit-ready!
                      </p>
                      <p className="text-sm text-emerald-300">
                        All evidence is complete and approved. Ready for external assurance.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 p-4 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center gap-3">
                    <AlertTriangle className="h-8 w-8 text-amber-400 shrink-0" />
                    <div>
                      <p className="text-base font-semibold text-amber-400">
                        ⚠️ {issues.length} issue(s) must be resolved
                      </p>
                      <p className="text-sm text-amber-300">
                        Address the issues below to achieve audit-ready status.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="text-center ml-8">
                <ScoreCircle score={readiness.overall_score} size="lg" />
                <p className={`text-sm font-semibold mt-2 ${
                  readiness.is_audit_ready ? 'text-emerald-400' :
                  readiness.overall_score >= 70 ? 'text-amber-400' : 'text-red-400'
                }`}>
                  {readiness.is_audit_ready ? 'Audit Ready' : 
                   readiness.overall_score >= 70 ? 'Needs Improvement' : 'Not Ready'}
                </p>
              </div>
            </div>
          </div>

          {/* Dimension Scores Grid */}
          <div className="grid md:grid-cols-3 gap-6">
            <DimensionCard
              icon={FileText}
              title="Evidence Completeness"
              score={readiness.scores?.evidence_completeness || 0}
              color="emerald"
              description={`${readiness.metrics?.claims_with_primary_evidence || 0} of ${readiness.metrics?.total_claims || 0} claims have primary evidence`}
            />
            
            <DimensionCard
              icon={Users}
              title="Approval Completeness"
              score={readiness.scores?.approval_completeness || 0}
              color="blue"
              description={`${readiness.metrics?.claims_fully_approved || 0} of ${readiness.metrics?.total_claims || 0} claims fully approved`}
            />
            
            <DimensionCard
              icon={BarChart3}
              title="Data Completeness"
              score={readiness.scores?.data_completeness || 0}
              color="purple"
              description="All required data points populated"
            />
            
            <DimensionCard
              icon={FileText}
              title="Documentation Quality"
              score={readiness.scores?.documentation_quality || 0}
              color="amber"
              description="Methodology and assumptions documented"
            />
            
            <DimensionCard
              icon={Shield}
              title="Audit Trail"
              score={readiness.scores?.audit_trail_completeness || 0}
              color="emerald"
              description={`${readiness.metrics?.audit_events_logged || 0} audit events logged`}
            />
            
            <DimensionCard
              icon={Hash}
              title="Cross-Framework Consistency"
              score={readiness.scores?.cross_framework_consistency || 0}
              color="teal"
              description="Same data used consistently across frameworks"
              highlight="Superpower #1"
            />
          </div>

          {/* Issues Section */}
          {issues.length > 0 && (
            <div className="bg-[#111111] rounded-xl border border-[#1F1F1F]">
              <div className="p-6 border-b border-[#1F1F1F]">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-400" />
                  Issues Requiring Attention
                  <span className="px-2 py-1 rounded bg-red-500/20 text-red-400 text-sm">
                    {issues.length}
                  </span>
                </h3>
                <p className="text-sm text-[#6B6B6B] mt-1">
                  Resolve these issues to achieve audit-ready status
                </p>
              </div>

              <div className="p-6 space-y-4">
                {issues.map((issue) => (
                  <IssueCard 
                    key={issue.issue_id} 
                    issue={issue} 
                    onResolve={handleResolveIssue}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {readiness.recommendations?.length > 0 && (
            <div className="bg-gradient-to-br from-[#111111] to-[#0A1628] rounded-xl border border-teal-500/20 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                💡 Recommended Actions
              </h3>

              <div className="space-y-3">
                {readiness.recommendations.map((rec, idx) => (
                  <RecommendationCard key={idx} rec={rec} />
                ))}
              </div>
            </div>
          )}

          {/* Audit Pack Status */}
          <div className="bg-[#0A0A14] rounded-xl p-6 border border-[#1F1F1F]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Package className="h-8 w-8 text-purple-400" />
                <div>
                  <p className="text-base font-semibold text-white mb-1">
                    {readiness.audit_pack_status?.can_generate_pack 
                      ? 'Ready to Generate Audit Pack'
                      : 'Audit Pack Not Yet Available'}
                  </p>
                  {readiness.audit_pack_status?.can_generate_pack ? (
                    <p className="text-sm text-[#A0A0A0]">
                      All requirements met. Generate complete audit pack for external assurance.
                    </p>
                  ) : (
                    <div>
                      {readiness.audit_pack_status?.blockers?.length > 0 && (
                        <div>
                          <p className="text-sm text-[#A0A0A0] mb-2">Blockers:</p>
                          <ul className="text-xs text-[#6B6B6B] list-disc list-inside space-y-1">
                            {readiness.audit_pack_status.blockers.slice(0, 3).map((blocker, idx) => (
                              <li key={idx}>{blocker}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {readiness.audit_pack_status?.estimated_ready_date && (
                        <p className="text-xs text-[#6B6B6B] mt-2">
                          Estimated ready: {new Date(readiness.audit_pack_status.estimated_ready_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {readiness.audit_pack_status?.can_generate_pack && (
                <button
                  onClick={() => window.location.href = `/audit/packs?cycle=${selectedCycle}&generate=true`}
                  className="px-6 py-3 rounded-lg bg-purple-500 hover:bg-purple-600 text-white font-semibold transition-colors flex items-center gap-2"
                >
                  Generate Pack
                  <ExternalLink size={16} />
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Live User Journeys */}
      <JourneySection title="Live Journeys">
        <JourneyPanel title="Auditor Verification Portal" ucId="UC-13"><AuditorPortalJourney /></JourneyPanel>
        <JourneyPanel title="RAGAS AI Quality Evaluation" ucId="UC-14"><RAGASEvaluation /></JourneyPanel>
      </JourneySection>
    </div>
  );
}
